import { SidePanel, TextButton } from '@wix/design-system';
import { RevertSmall } from '@wix/wix-ui-icons-common';
import React, { useContext, useState } from 'react';

import { ModalServiceContext, RicosContext } from './../../../ricos-context';
import type { LogBiFn } from './../../../_shared/bi-context-provider/bi-context-provider';
import { Panel, useClickOutside, usePanelContainerElement } from './../..';
import { CustomColorPicker } from './../../color-picker/new-color-picker/components/custom-color-picker';

import { PanelHeader } from '../../panel-header/panel-header';
import { SwatchColorPicker } from './components/swatch-color-picker';
import { CUSTOM_COLOR_PICKER_MODAL_ID } from './constants';
import {
  ADD_CUSTOM_COLOR_BUTTON,
  COLOR_PICKER_COLOR_ADDED,
  COLOR_PICKER_COLOR_PREVIEW,
  COLOR_PICKER_COLOR_SELECTED,
  FLOATING_COLOR_SWATCHES,
  RESET_BUTTON,
} from './data-hooks';
import classes from './new-color-picker-scss';

interface NewColorPickerProps {
  title: string;
  themeColors: string[];
  customColors: string[];
  currentColor: string | undefined;
  closeModal: () => void;
  onColorSelected: (color: string | undefined) => void;
  onColorAdded: (color: string) => void;
  onColorPreviewStart?: (color: string | undefined) => void;
  onColorPreviewEnd?: () => void;
  onResetColor: () => void;
  referenceElement: HTMLDivElement | null;
  logBi: LogBiFn;
}

export const NewColorPicker: React.FC<NewColorPickerProps> = ({
  title,
  themeColors,
  customColors,
  currentColor,
  closeModal,
  onColorSelected: onColorSelectedProp,
  onColorAdded: onColorAddedProp,
  onColorPreviewStart: onColorPreviewStartProp,
  onColorPreviewEnd,
  onResetColor: onResetColorProp,
  referenceElement,
  logBi,
}) => {
  const { t, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext);

  const containerEl = usePanelContainerElement();

  const [addButtonRef, setAddButtonRef] = useState<HTMLDivElement | null>(null);

  const isCustomColorPickerOpen = () => modalService.isModalOpen(CUSTOM_COLOR_PICKER_MODAL_ID);

  const onColorSelected = (color: string | undefined) => {
    onColorSelectedProp(color);
    logBi({ dataHook: COLOR_PICKER_COLOR_SELECTED, value: color });
  };

  const onColorAdded = (color: string) => {
    onColorAddedProp(color);
    logBi({ dataHook: COLOR_PICKER_COLOR_ADDED, value: color });
  };

  const onColorPreviewStart = (color: string | undefined) => {
    if (onColorPreviewStartProp) {
      onColorPreviewStartProp(color);
      logBi({ dataHook: COLOR_PICKER_COLOR_PREVIEW, value: color });
    }
  };

  const onResetColor = () => {
    onResetColorProp();
    logBi({ dataHook: RESET_BUTTON });
  };

  useClickOutside({
    containerRef: { current: containerEl },
    onClickOutside: (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null;

      if (
        target?.dataset?.hook === 'tooltip-text' ||
        target?.role === 'tooltip' ||
        containerEl.contains(target as Node) ||
        referenceElement?.contains(target as Node) ||
        isCustomColorPickerOpen()
      ) {
        return;
      }

      closeModal();
    },
  });

  const onAddClick = () => {
    modalService.togglePluginPanel({
      id: CUSTOM_COLOR_PICKER_MODAL_ID,
      Component: CustomColorPicker,
      componentProps: {
        currentColor,
        customColors,
        onColorPreviewStart,
        onColorPreviewEnd,
        onColorAdded,
        referenceElement: addButtonRef,
      },
      isMobile,
      positioning: {
        referenceElement: addButtonRef,
        placement: 'right-end',
      },
      type: undefined,
    });

    logBi({ dataHook: ADD_CUSTOM_COLOR_BUTTON });
  };

  const onCloseButtonClick = () => {
    onColorPreviewEnd?.();
    closeModal();
  };

  return (
    <Panel
      onCloseButtonClick={onCloseButtonClick}
      dataHook={FLOATING_COLOR_SWATCHES}
      width={isMobile ? '100%' : 288}
      maxHeight={isMobile ? undefined : 555}
      height="100%"
    >
      <PanelHeader title={title} className={classes.pickerHeader} />
      {
        <>
          <SwatchColorPicker
            currentColor={currentColor}
            themeColors={themeColors}
            customColors={customColors}
            onAddClick={onAddClick}
            onColorPreviewStart={onColorPreviewStart}
            onColorPreviewEnd={onColorPreviewEnd}
            onColorSelected={onColorSelected}
            setAddButtonRef={setAddButtonRef}
          />
          <SidePanel.Footer showDivider>
            <TextButton
              onClick={() => {
                onResetColor();
                logBi({ dataHook: RESET_BUTTON });
              }}
              weight="normal"
              size="small"
              dataHook={RESET_BUTTON}
              prefixIcon={<RevertSmall />}
            >
              {t('ColorPicker_SetToDefault_ButtonLabel')}
            </TextButton>
          </SidePanel.Footer>
        </>
      }
    </Panel>
  );
};
